import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import SaveReblastAttachment from '../../../API/SaveReblastAttachment';
import Tooltip from '../Tooltip';
import LogglyLogger from '../../../API/Loggly';
import resx from '../../../utilities/resx';
import { LOGGLY_LEVEL_ERROR, RESX_REBLASTS, RESX_GLOBAL } from '../../../Constants';
import { withMessage } from '../Message';

function CommentAttachment(props) {
    const [errorValidationError, setErrorValidationError] = useState(null);
    const refFileImg = useRef(null);
    const refFilePdf = useRef(null);

    function fileAttachementValidation(files, contentType) {
        let isFileCorrectType;
        const fileName = files.name;
        const fileSize = files.size;

        setErrorValidationError(null);

        if (!fileName || !fileSize || !contentType) return false;

        // extension validation
        const filExt = fileName.split('.').pop();
        const isValidExtension = /(jpeg|png|pdf|jpg|gif|tiff|tif|bmp)$/gi.test(filExt);

        if (!isValidExtension) {
            setErrorValidationError(resx(RESX_GLOBAL, 'INVALID_FILE_TYPE'));
            return false;
        }

        // size validation
        const isValidSize = !(fileSize <= 0 || fileSize > 20000000);

        if (!isValidSize) {
            setErrorValidationError(resx(RESX_REBLASTS, 'REBLAST_ATTACHMENT_ERROR'));
            return false;
        }

        // correct file type
        const isImage = /(jpeg|png|jpg|gif|tiff|tif|bmp)$/gi.test(filExt);
        const isPdf = /(pdf)$/gi.test(filExt);

        if ((isImage && contentType === '5') || (isPdf && contentType === '4')) {
            isFileCorrectType = false;
        } else if ((isImage && contentType === '4') || (isPdf && contentType === '5')) {
            isFileCorrectType = true;
        }

        if (!isFileCorrectType) {
            setErrorValidationError(resx(RESX_REBLASTS, 'REBLASTS_FILE_ATTACHMENT_ERROR'));
            return false;
        }

        return true;
    }

    function fileAttachementOnChange(e) {
        const contentTypeId = e.target.dataset.attachtype;
        const data = new FormData();

        data.append('file', e.target.files[0]);
        data.append('contenttypeid', contentTypeId);

        if (fileAttachementValidation(e.target.files[0], contentTypeId)) {
            // adding spinner
            const isLoadingTimeout = setTimeout(() => {
                props.update(prevState => ({ ...prevState, isUploadingAttachment: true }));
            }, 500);

            // save attchments
            SaveReblastAttachment.add(data)
                .then(response => {
                    const result = _path(['data'], response);

                    if (!result || !result.Success) {
                        throw new Error(result.Message);
                    }

                    const attachmentImagePreview = result.Data.AttachmentImage
                        ? result.Data.AttachmentImage[0].Url
                        : '/assets/home/images/adobe.png';
                    const commentsAttachmentsId = result.Data.AttachmentImage
                        ? result.Data.AttachmentImage[0].UniqueKey
                        : result.Data.AttachmentDocument.UniqueKey;
                    props.update(prevState => ({
                        ...prevState,
                        commentsAttachmentsId: commentsAttachmentsId,
                        commentsAttachmentsImage: attachmentImagePreview,
                        commentsAttachmentTypeId: contentTypeId
                    }));

                    // clear fileList
                    refFileImg.current.value = '';
                    refFilePdf.current.value = '';
                })
                .catch(error => {
                    props.message.addMessage({
                        message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'),
                        type: 'error'
                    });
                    LogglyLogger.add(
                        LOGGLY_LEVEL_ERROR,
                        'Saving comments attachment failure on the details page',
                        error
                    );
                })
                .then(() => {
                    clearTimeout(isLoadingTimeout);
                    props.update(prevState => ({ ...prevState, isUploadingAttachment: false }));
                });
        }
    }

    return (
        <React.Fragment>
            <li className="actions-add-attachments-wrapper pull-left">
                <ul className="actions-add-attachments list-inline">
                    <li
                        className="add-attachments add-photos attach-to-comment re-icon re-icon-attach-image"
                        onClick={() => refFileImg.current.click()}
                    >
                        <input
                            type="file"
                            ref={refFileImg}
                            accept="image/*"
                            className="upload-photo-input hide"
                            title="Click to attach an Image"
                            name="file"
                            encType="multipart/form-data"
                            data-attachtype="4"
                            onChange={fileAttachementOnChange}
                        />
                    </li>

                    <li className="add-video-attachments">
                        <Tooltip
                            overlay={
                                <div className="re-add-video-tooltip-desc">
                                    {resx(RESX_GLOBAL, 'VIDEO_ATTACHMENT_TOOLTIP')}
                                </div>
                            }
                            withArrow={true}
                            mixpanelType="video-attachment-tooltip"
                        >
                            <span className="add-attachments re-icon re-icon-video" />
                        </Tooltip>
                    </li>

                    <li
                        className="add-attachments add-pdf attach-to-comment re-icon re-icon-attach-pdf"
                        onClick={() => refFilePdf.current.click()}
                    >
                        <input
                            type="file"
                            ref={refFilePdf}
                            accept=".pdf"
                            className="upload-pdf-input hide"
                            title="Click to attach a PDF"
                            name="file"
                            encType="multipart/form-data"
                            data-attachtype="5"
                            onChange={fileAttachementOnChange}
                        />
                    </li>
                </ul>
            </li>
            {errorValidationError && <li className="re-comment-attachment-error"> {errorValidationError} </li>}
        </React.Fragment>
    );
}

CommentAttachment.propTypes = {
    message: PropTypes.object,
    data: PropTypes.object.isRequired,
    update: PropTypes.func.isRequired
};

export default withMessage(CommentAttachment);
